import ReactModal from 'react-modal'
import React, { useState } from 'react'

import IconTimes from 'assets/images/icon-times.svg'
import IconInformation from 'assets/images/icon-information.svg'
import BaseDialog from 'components/dialog'
import ButtonBase from 'components/partials/button'
import { customStyles } from 'constants/customStyles'
import { IAgencyGroup, IError } from '../../interface'
import { IAgency, IAgencyCSV, IMPORT_AGENCY_TO_AGENCY_BY_CSV_FILE } from 'queries/admin/agency'
import { useMutation } from '@apollo/client'
import { notify } from 'components/toastify'
import { STATUS } from 'constants/messages'
import InfiniteScroll from 'react-infinite-scroll-component'
import { parseCSVFile } from 'helpers/functionUtils'
import ReactTooltip from 'react-tooltip'
import { useSession } from 'helpers/checkSession'

interface Props {
  agencyGroup: IAgencyGroup
  isOpenDialog: boolean
  handleCloseDialog: () => void
}

const styleDialogDefault: ReactModal.Styles = {
  content: {
    ...customStyles.content,
    width: '700px',
  },
}

interface IFileError extends IError {
  messages: string[]
}

type RESULT_STATUS = 'Inserted' | 'Already Added' | 'Exist in other agency group'

interface IUploadResult extends IAgency {
  status: RESULT_STATUS
}

const allowedTypes = ['text/csv']
const maxSizeInMB = 5

const UploadAgencyFileDialog: React.FC<Props> = ({
  agencyGroup,
  isOpenDialog,
  handleCloseDialog,
}) => {
  const { handleErrorSession } = useSession()
  const [agencies, setAgencies] = useState<IAgencyCSV[]>([])
  const [isShowResult, setIsShowResult] = useState(false)
  const [uploadResult, setUploadResult] = useState<IUploadResult[]>([])
  const [error, setError] = useState<IFileError>({
    isError: false,
    message: null,
    messages: [],
  })

  const [importAgencyToAgencyGroup] = useMutation(IMPORT_AGENCY_TO_AGENCY_BY_CSV_FILE, {
    variables: {
      data: {
        agencyGroupId: agencyGroup.agencyGroupId,
        agencies,
      },
    },
    onCompleted: (data) => {
      notify('Import successfully', STATUS.SUCCESS)
      const uploadResultArr: IUploadResult[] = []

      for (const newAgency of data.importAgenciesToAgencyGroup.newAgencies) {
        const data: IUploadResult = { ...newAgency, status: 'Inserted' }
        uploadResultArr.push(data)
      }

      for (const existAgency of data.importAgenciesToAgencyGroup.existAgencies) {
        const data: IUploadResult = {
          ...existAgency,
          status:
            existAgency.agencyGroupId === agencyGroup.agencyGroupId
              ? 'Already Added'
              : 'Exist in other agency group',
        }
        uploadResultArr.push(data)
      }

      setUploadResult(uploadResultArr)
      setIsShowResult(true)
    },
    onError: (error) => {
      if (error.message) {
        handleErrorSession(error, error.message)
      } else {
        notify('Something wrong', STATUS.ERROR)
      }
    },
  })

  const onChangeFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return

    const errors = []
    const file = event.target.files[0]

    const fileSizeInMB = file.size / (1024 * 1024)

    if (!allowedTypes.includes(file.type)) {
      errors.push(`❌ Invalid file type.`)
    }

    if (fileSizeInMB > maxSizeInMB) {
      errors.push(`❌ File size exceeds ${maxSizeInMB}MB.`)
    }

    if (errors.length > 0) {
      setError({
        isError: true,
        message: null,
        messages: errors,
      })
      setAgencies([])
      return
    }

    setError({
      isError: false,
      message: null,
      messages: [],
    })

    const rows = await parseCSVFile<IAgencyCSV>(file)
    const agencies = rows.filter((r) => r.agencyKey || r.agencyName)
    setAgencies(agencies) // skip empty row
  }

  const onClickUploadBtn = async () => {
    importAgencyToAgencyGroup()
  }

  const onClickDownloadTemplateBtn = () => {
    const csv = 'agencyKey,agencyName\n1,agencyNameExample'
    const url = `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`
    const a = document.createElement('a')
    a.href = url
    a.download = 'agency_template.csv'
    a.click()
  }

  const onClickDownloadResultBtn = () => {
    const total = uploadResult.length
    const success = uploadResult.filter((d) => d.status === 'Inserted').length
    const error = uploadResult.filter(
      (d) => d.status === 'Already Added' || d.status === 'Exist in other agency group',
    ).length

    const lines = [
      `Agency Group,${agencyGroup.agencyGroupName}`,
      `total,${total}`,
      `success,${success},error,${error}`,
      'Agency Key,Agency Name,Status',
      ...uploadResult.map((a) => `${a.agencyKey},${a.agencyName},${a.status}`),
    ]

    // Build CSV content
    const csv = lines.join('\r\n')

    // Download
    const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'UploadAgencyResult.csv')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <BaseDialog
      modalIsOpen={isOpenDialog}
      customStyles={
        isShowResult
          ? { content: { ...styleDialogDefault.content, width: '1000px' } }
          : styleDialogDefault
      }
    >
      {isShowResult ? (
        /**Upload Result Dialog */
        <div className="flex flex-col overflow-y-scroll overflow-x-hidden px-[5px]">
          <div className="flex justify-between">
            <div />
            <img src={IconTimes} alt="" className="cursor-pointer " onClick={handleCloseDialog} />
          </div>
          <div className="flex justify-center pb-5">
            <p className="text-headline5A text-neutral-1">Upload result</p>
          </div>

          <div className="flex flex-col">
            <span>
              <strong>Agency Group: </strong>
              {agencyGroup.agencyGroupName}
            </span>
            <span>
              <strong>Total items in file:</strong> {agencies.length}
            </span>
          </div>

          {/**Header Table result */}
          <table className="w-full text-left border-2 border-neutral-6 rounded-t-lg overflow-hidden">
            <thead className="bg-primary-shade4 text-body1 text-neutral-2 md:pr-4">
              <tr>
                <th className="w-[20%] font-semibold py-5 pl-4">
                  <div className={`flex`}>
                    <span>Agency Key</span>
                  </div>
                </th>
                <th className="w-[60%] font-semibold py-5 pl-4">
                  <div className={`flex`}>
                    <span>Agency Name</span>
                  </div>
                </th>
                <th className="w-[20%] font-semibold py-5 pl-4">
                  <div className={`flex`}>
                    <span>Status</span>
                  </div>
                </th>
              </tr>
            </thead>
          </table>

          {/**Table result rows */}
          <InfiniteScroll
            dataLength={uploadResult.length}
            next={() => {}}
            hasMore={false}
            height={400}
            loader={<h4>Loading...</h4>}
          >
            <table className="cursor-pointer w-full">
              {uploadResult.map((item, index) => (
                <tbody key={index}>
                  <tr className="h-[72px] w-full hover:bg-neutral-7 pt-5">
                    <td className={`w-[20%] py-5 break-all pl-4 align-top`}>
                      <div>
                        <span>{item.agencyKey}</span>
                      </div>
                    </td>
                    <td className={`w-[50%] py-5 break-all pl-4 align-top`}>
                      <div>
                        <span>{item.agencyName}</span>
                      </div>
                    </td>
                    <td className={`w-[30%] py-5 break-all pl-4 align-top`}>
                      <div>
                        <span
                          className={`${
                            item.status === 'Inserted' ? 'text-green-500' : 'text-yellow-500'
                          }`}
                        >
                          {item.status}
                        </span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              ))}
            </table>
          </InfiniteScroll>

          <div className="flex justify-end pt-5 space-x-2">
            <ButtonBase type="submit" onClick={onClickDownloadResultBtn}>
              Download
            </ButtonBase>
            <ButtonBase
              type="submit"
              className="w-[120px] justify-around border-2 border-primary-1 text-primary-1 bg-white-50 hover:bg-primary-shade4"
              onClick={handleCloseDialog}
              bgDisabled="bg-primary-shade3"
            >
              Close
            </ButtonBase>
          </div>
        </div>
      ) : (
        /**Upload file */
        <div className="flex flex-col max-h-[600px] overflow-y-scroll overflow-x-hidden px-[5px]">
          <div className="flex justify-between">
            <div />
            <img src={IconTimes} alt="" className="cursor-pointer " onClick={handleCloseDialog} />
          </div>
          <div className="flex justify-center pb-5">
            <p className="text-headline5A text-neutral-1">Import agency to group via CSV File</p>
          </div>

          {/**Upload file button*/}
          <div>
            <label
              className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
              htmlFor="file_input"
            >
              Select csv file
            </label>
            <input
              className="w-full"
              aria-describedby="file_input_help"
              accept=".csv"
              id="file_input"
              type="file"
              onChange={onChangeFile}
            />
          </div>

          {error.isError && (
            <div className="flex flex-col">
              {error.messages.map((mess, index) => (
                <span key={`error-${index}`} className="text-red-500">
                  {mess}
                </span>
              ))}
            </div>
          )}

          <div className="flex">
            {agencies && <span>Total items: {agencies.length} </span>}

            {agencies.length === 0 && (
              <div className="flex justify-center">
                <a data-tip id="upload-tooltip" data-for="upload-tooltip" className="pl-1 flex">
                  <img src={IconInformation} alt="Icon-Information" className="w-[18px]" />
                </a>

                <ReactTooltip id="upload-tooltip" place="right" effect="solid" type="warning">
                  <span>{`Empty data or not follow the template. Please download the template below`}</span>
                </ReactTooltip>
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="flex justify-between mt-4">
            <ButtonBase className="justify-around" onClick={onClickDownloadTemplateBtn}>
              Download Template
            </ButtonBase>
            <div className="space-x-2">
              <ButtonBase
                onClick={onClickUploadBtn}
                className="w-[120px] justify-around"
                disabled={error.isError || agencies.length === 0}
                bgDisabled="bg-primary-shade3"
              >
                Upload
              </ButtonBase>
              <ButtonBase
                type="submit"
                className="w-[120px] justify-around border-2 border-primary-1 text-primary-1 bg-white-50 hover:bg-primary-shade4"
                onClick={handleCloseDialog}
                bgDisabled="bg-primary-shade3"
              >
                Close
              </ButtonBase>
            </div>
          </div>
        </div>
      )}
    </BaseDialog>
  )
}

export default UploadAgencyFileDialog
